import create from 'zustand';
import { fetchRequest } from './';
import { Api } from './api';
import { auth } from './auth';
import { UserApi } from './cache-manager';
import { DateInput, dateMedium, dateTimeFromInput, formatLocalDateTime, messageDateTimeFormat, utcDate, utcDateTimeString } from './date-time';
import { Person } from './domain';
import { formApi, FormApi } from './form';
import { personName } from './format';
import { VisitContext } from './visit-context';

type MessageConfiguration = {
  user?: Person;
  readonly?: boolean;
};
export type MessageType = 'received' | 'reply' | 'system';

export type Message<T = any> = {
  timestamp: string;
  sender: string;
  recipient: string;
  message: string;
  type: MessageType;
  metaData?: T;
  date?: DateInput;
  dayChange?: string;
  raw?: RawMessage;
};

export type MessageThread<T = any> = {
  messages: Message<T>[];
  date?: DateInput;
  timestamp?: string;
  userMessages?: {
    [key: string]: Message<T>[];
  };
  users?: string[];
};

type MessageData<T = any> = {
  nMesssages: number;
  user: Person;
  threads: MessageThread<T>[];
  reply: Message<T>;
  filtered: MessageThread<T>[];
  selected: MessageThread<T>;
};

type MessageActions<T = any, U = any> = {
  select(thread: MessageThread<T>): void;
  reply(message: string): void;
  filter(criteria: U): void;
  update(a: VisitContext): void;
};

type MessageForms = {
  reply: FormApi<{ message: string }>;
  filter: FormApi;
};

export type MessageApi = Api<MessageConfiguration, MessageData, MessageActions, MessageForms>;

type MessageProps = MessageConfiguration &
  Partial<Message> & {
    date?: DateInput;
    raw?: RawMessage;
  };

export function message<T>(props: MessageProps): Message<T> {
  const { message, recipient, date, type, sender, raw } = props;


  return {
    message,
    recipient,
    date,
    raw,
    timestamp: utcDateTimeString(date), //  formatLocalDate(date, messageDateTimeFormat),
    type: type || 'reply',
    sender: sender || 'You',
  };
}

export function createMessageApi(config: MessageConfiguration): MessageApi {
  const configuration = { ...{ readonly: false }, ...config };

  const data = create<MessageData>(() => ({
    nMesssages: 0,
    user: configuration.user,
    threads: [],
    reply: null,
    filtered: [],
    selected: null,
  }));

  function sender() {
    const u = data.getState().user;
    return u ? personName(u) : 'You';
  }
  let root: MessageThread;

  const actions: MessageActions = {
    select: (selected: MessageThread) => {
      data.setState({ selected });
    },
    reply: (v: string) => {
      const m = message({
        type: 'reply',
        message: v,
        sender: sender(),
        date: new Date(),
      });
      const s: MessageThread = data.getState().selected || { messages: [] };
      s.messages.push(m);

      data.setState({ reply: m, selected: { ...s } });
    },
    filter: (criteria: any) => {
      console.log(criteria);
      const filtered = [];
      data.setState({ filtered });
    },
    update: async (a: VisitContext) => {
      const { locationCode, accountNumber } = a.visit;
      const response = await messageRequest({ locationCode, accountNumber });

      let result: RawMessage[] = [];

      if (response?.body) {
        try {
          result = JSON.parse(response.body) || [];
        } catch (e) {
          console.info('error parsing message response', e);
        }
      }

      if (result?.length) {
        const users = []; // await resolveMessageUsers(result, a.users); // TODO: move user resolution to backend
        // console.log('update users', users);
        a.users.add(users);

        root = messageThread(result, a.users);
        messageApi.data.setState({
          selected: root,
          nMesssages: root.messages?.length,
        });

        if (root?.users.length) {
          setFilters(root.users);
        } else {
          setFilters(['All']);
        }
      } else {
        messageApi.data.setState({ selected: { messages: [] }, nMesssages: 0 });
      }
       //console.info('messageRequest', result)
    },
  };

  const reply = formApi({
    values: {
      message: null,
    },
    fields: {
      message: {
        label: 'Your Message',
        required: true,
        startIcon: 'person',
        multiline: true,
        rows: 2,
      },
    },
    valid: (v) => v.message && v.message.trim().length,
    onSubmit: (v) => {
      actions.reply(v.message);
    },
    resetValues: () => ({ message: null }),
  });

  const filter = formApi({
    values: {
      filter: 'All',
    },
    fields: {
      filter: {
        label: 'Filter Messages',
        required: true,
        list: [],
        listLabel: (v) => v,
        onChange: (v) => {
          const t = data.getState().selected;
          if (t?.userMessages) {
            messageApi.data.setState({
              selected: { messages: filterMessagesByDate(t.userMessages[v]) },
            });
          }
        },
      },
    },
    valid: (v) => v.filter && v.filter.trim().length,
    onSubmit: (v) => {},
    resetValues: () => ({ filter: null }),
  });

  function setFilters(a: string[]) {
    filter.controls.filter.list.setState({ value: a || [] });
  }

  const forms = {
    reply,
    filter,
  };

  return { configuration, data, actions, forms };
}

export function filterMessagesByDate(messages: Message[], asc = true): Message[] {
  if (!messages || !messages.length) {
    return [];
  }
  messages.forEach((v) => {
    
    v.date = dateTimeFromInput(v.date);
    v.timestamp = formatLocalDateTime(v.date, messageDateTimeFormat); // formatDate(v.date, messageDateTimeFormat);
    v.dayChange = null;
  });

  if (asc) {
    messages.sort((a, b) => (b.date as Date).valueOf() - (a.date as Date).valueOf());
  } else {
    messages.sort((a, b) => (a.date as Date).valueOf() - (b.date as Date).valueOf());
  }

  const dayChangeMap: { [keyof: string]: Message } = {};

  messages.forEach((v) => {
    const dayChange = dateMedium(v.date);
    if (!dayChangeMap[dayChange]) {
      dayChangeMap[dayChange] = v;
    }
  });

  Object.entries(dayChangeMap).forEach(([dayChange, message]) => (message.dayChange = dayChange));

  return messages;
}

type RawMessage = {
  id?: string;
  fromUser?: string;
  messageType?: string;
  departmentCode?: string;
  messageDateTime?: number[];
  messageText?: string;
  messageParentId?: string;
  visitId?: string;
  accountNumber?: string;
  worksheetId?: string;
  worksheetType?: string;
  locationCode?: string;
  processed?: true;
  recipientList?: string[];
  recipientAcknowledgements?: Array<{
    userId: string;
    acknowledged?: boolean;
  }>;
  quickResponseList?: [];
};

function messageDate(m: RawMessage) {
  if (!m?.messageDateTime?.length) {
    return new Date();
  }
  const a = m.messageDateTime;
  const utc = utcDate(a[0], a[1] - 1, a[2], a[3], a[4], a[5]);
  //  console.log('raw date - utc', m.messageDateTime, utc)
  return utc;
}

async function resolveMessageUsers(a: RawMessage[], api: UserApi) {
  let out = [];

  if (a?.length) {
    const users = await Promise.all(a.map((v) => api.list(v.recipientList || [])));
    users.forEach(async (v) => {
      api.add(v);
      out = out.concat(v);

      if (v?.length) {
        out.concat(v);
      }
    });
  }
  //  console.log('resolveMessageUsers', out)
  return out;
}

function messageThread(a: RawMessage[], api: UserApi): MessageThread {
  const users = api.all();

  function resolveName(id: string): string {
    const p: Person = users.filter((v) => v.userId === id)[0];
    if (!p) {
      return id;
    }
    return p.firstName + ' ' + p.lastName;
  }

  function resolveUsers(ids: string[]): string {
    if (!ids?.length) {
      return '';
    }
    return ids.map(resolveName).join(', ');
  }

  const out = [];

  const userMessages = {};

  for (let i = 0; i < a?.length; i++) {
    const raw: RawMessage = a[i];

    if (raw) {
      const { fromUser, recipientList } = raw;
      const sender = resolveName(fromUser);
      const s = raw?.messageText || '';
      const msg = s.substring(s.indexOf('Description:'));
      const m = message({
        raw,
        sender,
        message: msg,
        date: messageDate(raw),
        recipient: resolveUsers(recipientList),
      });

      out.push(m);
      const list = userMessages[sender] || [];
      list.push(m);
      userMessages[sender] = list;
    }
  }
  userMessages['All'] = filterMessagesByDate(out);

  return {
    userMessages,
    users: Object.keys(userMessages),
    messages: userMessages['All'],
  };
}

export const messageApi = createMessageApi({ user: auth?.signedIn || {} });

/*
DEV
https://dev.coding-presidiohealth.com/messaging/message?locationCode=INNOVA01&accountNumber=4092347732
PROD
https://prod.coding-presidiohealth.com/messaging/message?locationCode=INNOVA01&accountNumber=4090744203
*/

/*
You'd need to pass the Authorization token, same way as for any other API GW api
For filtering you can use any combination of "locationCode", "accountNumber", "visitId", "worksheetId" fields
Messages are sorted asc by messageDateTime field
*/

type MessageArgs = {
  locationCode?: string;
  accountNumber?: string;
};

const url = ({ locationCode, accountNumber }: MessageArgs) => `/messaging/okta/message?locationCode=${locationCode}&accountNumber=${accountNumber}`;

export const messageRequest = fetchRequest<MessageArgs, any>({
  method: 'get',
  url,
});
